import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";
import { CheckboxSchemeDefinition, CheckboxSchemeName } from "./constants/checkbox-types";
import { CHECKBOX_SCHEME_CONFIG, CHECKBOX_SCHEME_MAP } from "./constants/checkbox-constants";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
import { KeyIds } from "../../constants/keys";
import { FormComponent, handleCustomValidationDelegate, validateDelegate } from "../../common/form-component";
import {
  DefaultLocalization,
  LocalizableComponent,
  Localization,
  setCurrentLocalizationDelegate
} from "../../common/localizable-component";
import pfx from "../../utils/style-class-prefix-helper";
import { FORM_LOCALIZATION } from "../form/constants/form-i18n";
import { CheckboxSchemeConfiguration } from "../../constants/colors";

/**
 * @slot info-icon - Holds a single eon-ui-info-icon element
 * @slot html-label - contents of the label element
 */
@Component({
  tag: "eon-ui-checkbox",
  styleUrl: "checkbox.scss",
  shadow: true
})
export class Checkbox
  implements
    ColorableComponent<CheckboxSchemeConfiguration<CheckboxSchemeDefinition>>,
    LocalizableComponent<DefaultLocalization>,
    FormComponent<boolean, DefaultLocalization> {
  schemeConfig: CheckboxSchemeConfiguration<CheckboxSchemeDefinition> = CHECKBOX_SCHEME_CONFIG;
  schemeMap = CHECKBOX_SCHEME_MAP;

  localization: Localization<DefaultLocalization> = FORM_LOCALIZATION;
  currentLocalization: DefaultLocalization;

  componentIcon: HTMLElement;
  componentLabel: HTMLElement;

  @Element() hostElement: HTMLEonUiCheckboxElement;

  @State() activeScheme: CheckboxSchemeDefinition;

  /**
   * Raised after the Checkbox has been clicked. Returns a boolean whether the Checkbox is now checked or not.
   */
  @Event({ bubbles: false }) valueChanged: EventEmitter<boolean>;

  /**
   * **Internal use only!**
   * &nbsp;<br>
   * Please don't change this.
   * @disableControlInStorybook true
   */
  @Prop({ reflect: true }) isFormComponent: boolean = true;

  /**
   * The name Attribute is a string that refers to the native name attribute of an HtmlInputElement.
   * @example <eon-ui-checkbox name="group1"/>
   */
  @Prop() name: string;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop() scheme: CheckboxSchemeName;

  /**
   * The label that appears on the right hand side of the checkbox. The label is a string that gives a description on what the value of the checkbox means.
   * If you need HTML support, provide the label via the slot "html-label".
   * @example <eon-ui-checkbox label="Agree to terms of service"></eon-ui-checkbox>
   */
  @Prop() label: string;

  /**
   * Define the size of the input element
   */
  @Prop() size: "small" | "normal" = "normal";

  /**
   * Set if the Checkbox is required
   * @example <eon-ui-checkbox required="true"></eon-ui-checkbox>
   */
  @Prop() required: boolean;

  /**
   * Wether or not the checkbox is disabled. Disabled checkboxes behave like readonly native html-checkboxes.
   * See [MDN Docs - checkbox](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input/checkbox) for the readonly attribute.
   * The color of the labels and error is also changed to indicate this state.
   * @example <eon-ui-checkbox label="undo-time" disabled>
   */
  @Prop({ reflect: true }) disabled: boolean = false;

  /**
   * Wether or not the checkbox is checked. Changes if the checkbox is clicked.
   * @example <eon-ui-checkbox label="I want this to be checked from the beginning" checked />
   */
  @Prop({ reflect: true, mutable: true }) checked: boolean = false;

  /**
   * Native html value attribute. Changes the key the checkbox value is stored in form submissions. "on" is the standard value.
   * See [MDN Docs - checkbox](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/Input/checkbox)
   * @example <eon-ui-checkbox value="use-experimental-api" label="Do you want to use the experimental api?">
   */
  @Prop() value: string = "on";

  /**
   * **Internal use only! Please don't use this property directly.   **
   * &nbsp;<br>
   * Wether or not the checkbox is invalid. If the checkbox is invalid, the error message is displayed.
   * Otherwise, the error message is hidden.
   * @example <eon-ui-checkbox label="Very important, terms of service" invalid error-message="You need to check this">
   */
  @Prop({ reflect: true, mutable: true }) invalid: boolean = false;

  /**
   * **Internal use only! Please don't use this property directly.   **
   * &nbsp;<br>
   * The error message that is displayed if the checkbox is invalid. Is listed below the label and sublabel. Hidden (display: none) otherwise.
   * @example <eon-ui-checkbox label="Very important, terms of service" invalid error-message="You need to check this">
   */
  @Prop() errorMessage: string;

  /**
   * An empty Placeholder for an optional custom Error Message for CustomValidation.
   * @example <eon-ui-checkbox custom-error-message="this is an custom error message"></eon-ui-checkbox>
   */
  @Prop({ mutable: true }) customErrorMessage: string;

  /**
   * A custom message to show when the required field has no value
   */
  @Prop() customRequiredMessage: string;

  private get requiredMessage(): string {
    return this.customRequiredMessage ? this.customRequiredMessage : this.currentLocalization.required;
  }

  /**
   * Whether or not an existing CustomValidator has and error.
   */
  @State() customValidatorHasError: boolean = false;

  @State() isInfoIconAvailable: boolean;
  labelAvailable: boolean = false;

  /**
   * Whether this button can be focused by keyboard. Defaults to true.
   */
  @Prop() navigable: boolean = true;

  componentWillLoad() {
    this.applyScheme();
    this.setCurrentLocalization();
    this.checkIfLabelExists();
    this.isInfoIconAvailable = !!this.hostElement.querySelector('[slot="info-icon"]');
  }

  componentDidLoad() {
    this.setCssVariables();
    this.attachHoverEventListener();
  }

  @Watch("scheme")
  applyScheme() {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  setCurrentLocalization() {
    setCurrentLocalizationDelegate(this);
  }

  /**
   * Saves the given error message and assumes that the component is invalid if any error message is provided.
   * @param component The FormComponent that should handle the custom validation.
   * @param errorMessage An error message of a custom validator. If none is supplied, the custom validation is considered to be valid.
   */
  @Method()
  async handleCustomValidation(errorMessage: string) {
    await handleCustomValidationDelegate(this, errorMessage);
  }

  /**
   * Validates that the component has a value if the required attribute is set.
   */
  async validateRequired(): Promise<boolean> {
    const isInvalid = this.required && !this.checked;
    this.errorMessage = isInvalid ? this.requiredMessage : "";
    return !isInvalid;
  }

  /**
   * Combines the result of the internal required validator and the custom validator.
   * Sets a default error message if required validation failed or takes the custom validation error message otherwise.
   */
  @Watch("required")
  @Method()
  async validate(): Promise<boolean> {
    return await validateDelegate(this);
  }

  /**
   * Update custom-required-message
   */
  @Watch("customRequiredMessage")
  setCustomRequiredMessage() {
    this.validate();
  }

  private handleChange(e: Event) {
    const isChecked = e.target["checked"];
    this.checked = isChecked;
  }

  private handleKeyDown(e: KeyboardEvent) {
    if (e.key == KeyIds.SPACE) {
      e.preventDefault();
      this.checked = !this.checked;
    }
  }

  @Watch("checked")
  handleChangeInternally(isChecked: boolean) {
    if (this.disabled) {
      return;
    }
    if (this.checked != isChecked) {
      this.checked = isChecked;
    }
    // an invalid checkbox must be valid after its state changed, as it has only two states and one of two states must be valid.
    if (this.invalid) {
      this.invalid = false;
    }

    this.valueChanged.emit(this.checked);
  }

  @Watch("label")
  checkIfLabelExists() {
    this.labelAvailable = !!this.label || !!this.hostElement.querySelector('[slot="html-label"]');
  }

  attachHoverEventListener() {
    this.componentLabel.addEventListener("mouseenter", () => {
      this.componentIcon.classList.add("force-hover");
    });

    this.componentLabel.addEventListener("mouseleave", () => {
      this.componentIcon.classList.remove("force-hover");
    });
  }

  render() {
    return (
      <Host onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}>
        <div class={pfx({ checkbox: true, [this.size]: this.size })}>
          <label ref={(el) => (this.componentLabel = el as HTMLElement)}>
            <input
              type="checkbox"
              value={this.value}
              name={this.name}
              onChange={(event: Event) => this.handleChange(event)}
              checked={this.checked}
              disabled={this.disabled}
              required={this.required}
            />
            <div
              ref={(el) => (this.componentIcon = el as HTMLElement)}
              class={pfx({
                "icon-carrier": true,
                checked: this.checked,
                invalid: this.invalid,
                disabled: this.disabled
              })}
              tabIndex={!this.disabled && this.navigable ? 0 : -1}
            >
              <eon-ui-icon
                aria-label="radio_checkbox_tick"
                class={pfx("icon-content")}
                name="radio_checkbox_tick"
                size="small"
                alignment-x="center"
                alignment-y="center"
              ></eon-ui-icon>
            </div>
            {this.labelAvailable && (
              <span
                class={pfx({
                  label: true,
                  disabled: this.disabled
                })}
                innerHTML={!this.hostElement.querySelector('[slot="html-label"]') ? this.label : undefined}
              >
                <slot name="html-label"></slot>
              </span>
            )}
          </label>
          {this.isInfoIconAvailable && (
            <span class={pfx("checkbox-info-icon")}>
              <slot name="info-icon" />
            </span>
          )}
        </div>
        {!this.disabled && this.invalid && (
          <eon-ui-form-message class={pfx("error")} type="error" message={this.errorMessage}></eon-ui-form-message>
        )}
      </Host>
    );
  }
}
